/* eslint-disable prettier/prettier */
import { UseFormReturnType } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useState, useEffect, useCallback } from "react";
import { getEmpresaDataByCnpj } from "@/services/brasilapi";
import { CreateLicenciadaFormData } from "../../../schema/create-licenciada-schema";

type EntityType = "novaLicenciada" | "novaEmpresa";

const TAM_CNPJ = 14;

export const useCnpjLookup = (form: UseFormReturnType<CreateLicenciadaFormData>, entityType: EntityType) => {
  const [isFetching, setIsFetching] = useState(false);
  const [lastFetchedCnpj, setLastFetchedCnpj] = useState<string>("");

  const fetchDataByCnpj = useCallback(async (cnpj: string) => {
    // Evita buscar o mesmo CNPJ novamente
    if (cnpj === lastFetchedCnpj) return;
    
    try {
      setIsFetching(true);
      setLastFetchedCnpj(cnpj);
      const data = await getEmpresaDataByCnpj(cnpj);

      if (!data || !data.razao_social || data.razao_social === "ERRO") {
        showNotification({
          message:
            data?.razao_social === "ERRO"
              ? "Base de consulta indisponível no momento. Cadastre os dados manualmente."
              : `O CNPJ ${cnpj} não foi encontrado na base nacional de empresas.`,
          color: "red",
          autoClose: 2500
        });
        return;
      }

      // Monta o objeto de atualização baseado no tipo de entidade
      const baseUpdate = {
        nomeFantasia: data.nome_fantasia,
        razaoSocial: data.razao_social,
        email: form.values[entityType].email || data.email || "",
        telefone: data.ddd_telefone_1,
        endereco: {
          ...form.values[entityType].endereco,
          cep: data.cep,
          logradouro: data.logradouro,
          numero: data.numero,
          complemento: data.complemento,
          bairro: data.bairro,
          cidade: data.municipio,
          estado: data.uf
        }
      };

      // Adiciona campos específicos se for empresa
      const entityUpdate =
        entityType === "novaEmpresa"
          ? {
              ...baseUpdate,
              codigoCNAE: data.cnae_fiscal.toString(),
              cnae: data.cnae_fiscal_descricao
            }
          : baseUpdate;

      form.setValues((values: CreateLicenciadaFormData) => ({
        ...values,
        [entityType]: {
          ...values[entityType],
          ...entityUpdate
        }
      }));
    } finally {
      setIsFetching(false);
    }
  }, [form, entityType, lastFetchedCnpj]);

  // Watch for CNPJ changes and trigger lookup automatically
  useEffect(() => {
    const cnpjValue = form.values[entityType].cnpj;
    if (!cnpjValue) return;

    const cnpj = cnpjValue.replaceAll(/\D/g, "");
    if (cnpj.length === TAM_CNPJ && cnpj !== lastFetchedCnpj) {
      fetchDataByCnpj(cnpj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values[entityType].cnpj, lastFetchedCnpj]);

  return { isFetching, fetchDataByCnpj };
};
